import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { getLanguagePair } from '@/config/languages';
import { getUiLocaleForRoute } from '@/i18n/uiLanguage';
import { reportError } from '@/services/errorTracking';

interface UiLanguageSyncProps {
  currentLanguagePair: string;
  immersionMode: boolean;
  signedIn: boolean;
}

export function UiLanguageSync({
  currentLanguagePair,
  immersionMode,
  signedIn,
}: UiLanguageSyncProps) {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const locale = getUiLocaleForRoute(pathname, currentLanguagePair, immersionMode, signedIn);

  useEffect(() => {
    // Recheck on language changes so a late resource load cannot restore a prior route's locale.
    if (locale !== i18n.language) {
      i18n.changeLanguage(locale).catch((error: unknown) => {
        reportError(error, { operation: 'app.changeUiLanguage' });
      });
    }
  }, [i18n, i18n.language, locale]);

  useEffect(() => {
    // Bilingual learning screens need the target copy after sign-in. Public
    // screens keep their native locale; Try uses its own sample-card content.
    if (!signedIn) return;

    const pair = getLanguagePair(currentLanguagePair);
    if (pair) {
      i18n.loadLanguages(pair.targetCode.toLowerCase()).catch((error: unknown) => {
        reportError(error, { operation: 'app.preloadTargetLanguage' });
      });
    }
  }, [currentLanguagePair, i18n, signedIn]);

  return null;
}
